import React, { memo, useCallback, useMemo } from "react";
import { IconButton, Stack, Tooltip } from "@mui/material";
import { MANUALLY_CREATED_STATUS } from "../../../referral/constants/referral.constants";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ViewIcon from "@mui/icons-material/Visibility";
import NotesIcon from "@mui/icons-material/EventNote";
import CompleteIcon from "@mui/icons-material/TaskAlt";

const ActiveReferralActionsCell = ({ row, type = "sent", declined = false, completed = false }) => {
  const { user, provider, status, id, actions } = row;

  const canSendReferral = user?.referral_permissions?.some(
    (e) => e.key === "SEND_REFERRAL" && e.value
  );
  const isDraft = status?.toLowerCase() === "draft";

  const handleClick = useCallback(
    (e, action, extraArg) => {
      e.stopPropagation();
      if (typeof action !== "function") return;
      action(id, extraArg);
    },
    [id]
  );

  const isManuallyCreated = status === MANUALLY_CREATED_STATUS;

  const allButtons = useMemo(
    () => ({
      update: {
        label: "Update Draft",
        show: canSendReferral && isDraft,
        color: "primary",
        icon: <EditIcon fontSize="small" />,
        onClick: (e) => handleClick(e, actions.onUpdate),
      },
      delete: {
        label: "Delete",
        show: canSendReferral && isDraft,
        color: "error",
        icon: <DeleteIcon fontSize="small" />,
        onClick: (e) => handleClick(e, actions.onDelete),
      },
      view: {
        label: "View",
        show: (provider || isManuallyCreated) && !(canSendReferral && isDraft),
        color: "primary",
        icon: <ViewIcon fontSize="small" />,
        onClick: (e) => handleClick(e, actions?.onViewReferral || actions?.onView),
      },
      notes: {
        label: "View Notes",
        show: provider || isManuallyCreated,
        color: "primary",
        icon: <NotesIcon fontSize="small" />,
        onClick: (e) => handleClick(e, actions.onViewNotes),
      },
      review: {
        label: "Review",
        show: type === "received",
        color: "primary",
        icon: <ViewIcon fontSize="small" />,
        onClick: (e) => handleClick(e, actions.onReview || actions.onViewReferral || actions.onView, row),
      },
      complete: {
        label: "Complete",
        show: (!declined && !completed) &&
          ((type === "sent" && !isDraft) ||
          (type === "received" &&
            ["declined", "scheduled", "ref_completed"].includes(status))),
        color: "primary",
        icon: <CompleteIcon fontSize="small" />,
        onClick: (e) => handleClick(e, actions.onComplete),
      },
    }),
    [canSendReferral, isManuallyCreated, isDraft, provider, type, status, handleClick, actions, row, declined, completed]
  );

  const buttonOrder =
    type === "sent"
      ? ["update", "delete", "view", "notes", "complete"]
      : ["review", "notes", "complete"];

  return (
    <Stack direction="row" spacing={0.5}>
      {buttonOrder
        .map((key) => allButtons[key])
        .filter((btn) => btn.show)
        .map((btn, index) => (
          <Tooltip key={index} title={btn.label} arrow>
            <IconButton
              color={btn.color}
              size="small"
              onClick={btn.onClick}
            >
              {btn.icon}
            </IconButton>
          </Tooltip>
        ))}
    </Stack>
  );
};

export default memo(ActiveReferralActionsCell);
